import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Toast } from "primereact/toast";
import Cookies from "js-cookie";
import axios from "axios";
import { Default } from "../../layouts/default";
import Loader from "../../common/loader";
import LazyImage from "../../common/LazyImage";
import { Context as AuthContext } from "../../../context/AuthContext";
import { axiosAuth } from "../../../utils/axiosInstance";
import bannerBv from "../../../image/banner-bv.png";
import "../../../css/allFacility.css";
import "../../../css/tournaments.css";
import "../../../css/tournament-register.css";

const BASE_URL = process.env.REACT_APP_API_ENDPOINT;
const PENDING_TOURNAMENT_KEY = "bv_pending_tournament_register";
const TEAM_FIELDS = [
  {
    name: "team_name",
    label: "Team name",
    type: "text",
    required: true,
    placeholder: "Enter team name",
    icon: "fa-shield-halved",
  },
  {
    name: "captain_jersey_number",
    label: "Captain jersey number",
    type: "number",
    required: true,
    placeholder: "e.g. 10",
    icon: "fa-hashtag",
  },
];

const emptyPlayer = () => ({
  player_name: "",
  phone: "",
  email: "",
  jersey_number: "",
});

const parseFee = (fee) => {
  if (fee === null || fee === undefined || fee === "") return 0;
  const numeric = String(fee).replace(/[^0-9.]/g, "");
  return Number(numeric) || 0;
};

const formatFee = (fee) => {
  const amount = parseFee(fee);
  if (!amount) return "Free";
  return `₹${amount.toLocaleString("en-IN")}`;
};

const formatTournamentDate = (startDate, endDate) => {
  if (!startDate || !endDate) return "";

  const start = new Date(startDate);
  const end = new Date(endDate);

  return `${start.getDate()} ${start.toLocaleString("en-US", {
    month: "short",
  })} - ${end.getDate()} ${end.toLocaleString("en-US", {
    month: "short",
  })}, ${end.getFullYear()}`;
};

const pick = (...values) =>
  values.find((v) => v !== undefined && v !== null && String(v).trim() !== "") ?? "";

// Reads name / email / phone from whatever shape the auth state uses
const getCaptainDetails = (userData) => {
  const user = userData?.user || userData || {};
  const profile = user.profile || userData?.profile || {};
  return {
    name: pick(
      user.name,
      profile.name,
      user.full_name,
      [user.first_name, user.last_name].filter(Boolean).join(" "),
      user.username,
    ),
    email: pick(user.email, profile.email),
    phone: pick(
      user.phone,
      user.mobile,
      user.contact,
      user.mobile_number,
      profile.contact,
      profile.phone,
      profile.mobile,
    ),
  };
};

// Works whether the API returns { order: {...} } or just { order_id: "..." }
const extractOrder = (data = {}) => {
  const raw = data.order || data.razorpay_order || data.data?.order || null;
  if (raw && typeof raw === "object") return raw;

  const orderId =
    (typeof raw === "string" ? raw : null) ||
    data.order_id ||
    data.razorpay_order_id ||
    data.data?.order_id ||
    null;

  if (!orderId) return null;
  return { id: orderId, amount: data.amount, currency: data.currency };
};

const RegisterTournament = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useRef(null);
  const { state, openLoginModal } = useContext(AuthContext);

  const isAuthed = !!Cookies.get("USER_TOKEN") || !!state.userData;

  const [tournament, setTournament] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successState, setSuccessState] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [form, setForm] = useState({
    team_name: "",
    captain_jersey_number: "",
  });
  const [players, setPlayers] = useState([]);
  const [errors, setErrors] = useState({});
  const [playerModalOpen, setPlayerModalOpen] = useState(false);
  const [editingPlayerIndex, setEditingPlayerIndex] = useState(null);
  const [playerDraft, setPlayerDraft] = useState(emptyPlayer());
  const [playerDraftErrors, setPlayerDraftErrors] = useState({});

  const maxPlayers = useMemo(
    () => Number(tournament?.max_players_per_team) || 20,
    [tournament],
  );
  const entryAmount = parseFee(tournament?.entry_fee);

  const captain = useMemo(() => {
    const fromProfile = getCaptainDetails(userProfile);
    const fromState = getCaptainDetails(state.userData);
    return {
      name: fromProfile.name || fromState.name,
      email: fromProfile.email || fromState.email,
      phone: fromProfile.phone || fromState.phone,
    };
  }, [userProfile, state.userData]);
  const captainFields = [
    { key: "name", label: "Captain name", value: captain.name, icon: "fa-user" },
    { key: "phone", label: "Captain mobile", value: captain.phone, icon: "fa-phone" },
    {
      key: "email",
      label: "Captain email",
      value: captain.email,
      icon: "fa-envelope",
      full: true,
    },
  ];

  // The captain is part of the squad, so extra players = total - 1
  const maxExtraPlayers = Math.max(0, maxPlayers - 1);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!isAuthed) {
      sessionStorage.setItem(PENDING_TOURNAMENT_KEY, String(id));
      openLoginModal(true);
    }
  }, [isAuthed, id, openLoginModal]);

  useEffect(() => {
    if (state.userData) {
      sessionStorage.removeItem(PENDING_TOURNAMENT_KEY);
    }
  }, [state.userData]);

  useEffect(() => {
    const fetchTournament = async () => {
      try {
        setIsLoading(true);
        const response = await axios.get(`${BASE_URL}/get-tournament-by-id/${id}`);
        setTournament(response.data.tournament || null);
        setPlayers([]);
      } catch (error) {
        console.error("Error loading tournament:", error);
        setTournament(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTournament();
  }, [id]);

  // Fetch the logged-in user's details (same API the header uses)
  useEffect(() => {
    if (!isAuthed) {
      setUserProfile(null);
      return undefined;
    }

    let cancelled = false;
    (async () => {
      try {
        setProfileLoading(true);
        const response = await axiosAuth.get("get-user-details");
        if (!cancelled && response.status === 200 && response.data?.user) {
          setUserProfile(response.data.user);
        }
      } catch (error) {
        if (!cancelled) console.error("Error fetching user details:", error);
      } finally {
        if (!cancelled) setProfileLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isAuthed]);

  // Lock page scroll + close on Esc while the player modal is open
  useEffect(() => {
    if (!playerModalOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") {
        setPlayerModalOpen(false);
        setEditingPlayerIndex(null);
        setPlayerDraft(emptyPlayer());
        setPlayerDraftErrors({});
      }
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [playerModalOpen]);

  const updateField = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const closePlayerModal = () => {
    setPlayerModalOpen(false);
    setEditingPlayerIndex(null);
    setPlayerDraft(emptyPlayer());
    setPlayerDraftErrors({});
  };

  const openAddPlayer = () => {
    if (players.length >= maxExtraPlayers) {
      toast.current?.show({
        severity: "warn",
        summary: "Squad full",
        detail: `This tournament allows a maximum of ${maxPlayers} players including the captain.`,
        life: 2500,
      });
      return;
    }
    setEditingPlayerIndex(null);
    setPlayerDraft(emptyPlayer());
    setPlayerDraftErrors({});
    setPlayerModalOpen(true);
  };

  const openEditPlayer = (index) => {
    setEditingPlayerIndex(index);
    setPlayerDraft({ ...players[index] });
    setPlayerDraftErrors({});
    setPlayerModalOpen(true);
  };

  const updatePlayerDraft = (name, value) => {
    setPlayerDraft((prev) => ({ ...prev, [name]: value }));
    setPlayerDraftErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const savePlayer = () => {
    const nextErrors = {};
    if (!playerDraft.player_name.trim()) nextErrors.player_name = "Name is required.";
    if (!/^\d{10}$/.test(playerDraft.phone)) nextErrors.phone = "Enter a 10-digit phone.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(playerDraft.email.trim())) {
      nextErrors.email = "Enter a valid email.";
    }
    if (playerDraft.jersey_number === "" || Number(playerDraft.jersey_number) < 0) {
      nextErrors.jersey_number = "Jersey number is required.";
    }

    const duplicatePhone = players.some(
      (player, index) =>
        player.phone === playerDraft.phone && index !== editingPlayerIndex,
    );
    const duplicateEmail = players.some(
      (player, index) =>
        player.email.toLowerCase() === playerDraft.email.trim().toLowerCase() &&
        index !== editingPlayerIndex,
    );
    const duplicateJersey =
      playerDraft.jersey_number !== "" &&
      (players.some(
        (player, index) =>
          Number(player.jersey_number) === Number(playerDraft.jersey_number) &&
          index !== editingPlayerIndex,
      ) ||
        (form.captain_jersey_number !== "" &&
          Number(form.captain_jersey_number) === Number(playerDraft.jersey_number)));

    if (duplicatePhone) nextErrors.phone = "This phone is already on the squad.";
    if (duplicateEmail) nextErrors.email = "This email is already on the squad.";
    if (duplicateJersey) nextErrors.jersey_number = "This jersey number is already taken.";

    setPlayerDraftErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    const nextPlayer = {
      ...playerDraft,
      player_name: playerDraft.player_name.trim(),
      email: playerDraft.email.trim(),
    };

    setPlayers((prev) =>
      editingPlayerIndex === null
        ? [...prev, nextPlayer]
        : prev.map((player, index) =>
            index === editingPlayerIndex ? nextPlayer : player,
          ),
    );

    closePlayerModal();
  };

  const removePlayer = (index) => {
    setPlayers((prev) => prev.filter((_, playerIndex) => playerIndex !== index));
  };

  const validate = () => {
    const nextErrors = {};

    TEAM_FIELDS.forEach((field) => {
      const value = String(form[field.name] || "").trim();
      if (field.required && !value) {
        nextErrors[field.name] = `${field.label} is required.`;
      }
    });

    if (
      form.captain_jersey_number !== "" &&
      Number(form.captain_jersey_number) < 0
    ) {
      nextErrors.captain_jersey_number = "Enter a valid jersey number.";
    } else if (
      form.captain_jersey_number !== "" &&
      players.some(
        (p) => Number(p.jersey_number) === Number(form.captain_jersey_number),
      )
    ) {
      nextErrors.captain_jersey_number =
        "This jersey number is already used by a player.";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const openRazorpay = (order, key) => {
    if (!window.Razorpay) {
      toast.current?.show({
        severity: "error",
        summary: "Payment unavailable",
        detail: "Payment gateway could not be loaded. Please refresh and try again.",
        life: 3000,
      });
      setIsSubmitting(false);
      return;
    }

    const options = {
      key: key || process.env.REACT_APP_RAZORPAY_KEY_ID,
      // Razorpay needs the amount in paise
      amount: Number(order.amount) || entryAmount * 100,
      currency: order.currency || "INR",
      name: "BookVenue",
      description: `${tournament?.name || "Tournament"} registration`,
      image: "https://bookvenue.app/final-bv-logo.png",
      order_id: order.id,
      handler: async (response) => {
        try {
          setIsSubmitting(true);
          const apiResponse = await axiosAuth.post(
            `${BASE_URL}/verify-team-payment`,
            {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            },
          );

          if (apiResponse.status === 200) {
            setSuccessState({
              teamName: form.team_name,
              paid: true,
            });
            toast.current?.show({
              severity: "success",
              summary: "Registered",
              detail: "Payment successful. Your team is registered.",
              life: 3000,
            });
          } else {
            throw new Error("Payment verification failed");
          }
        } catch (error) {
          toast.current?.show({
            severity: "error",
            summary: "Verification failed",
            detail:
              error.response?.data?.message ||
              "Payment was taken but could not be verified. Please contact support.",
            life: 4000,
          });
        } finally {
          setIsSubmitting(false);
        }
      },
      notes: order.notes || {},
      theme: { color: "#0d9488" },
      modal: {
        ondismiss: () => {
          setIsSubmitting(false);
          toast.current?.show({
            severity: "warn",
            summary: "Payment cancelled",
            detail: "Payment was not completed. Please try again.",
            life: 3000,
          });
        },
      },
    };

    const rzp = new window.Razorpay(options);
    rzp.on("payment.failed", (failure) => {
      console.error("Razorpay payment failed:", failure?.error);
      setIsSubmitting(false);
      toast.current?.show({
        severity: "error",
        summary: "Payment failed",
        detail: failure?.error?.description || "Please try again to complete registration.",
        life: 3500,
      });
    });
    rzp.open();
  };

  const handleSubmit = async (event) => {
    event?.preventDefault?.();

    if (!isAuthed) {
      sessionStorage.setItem(PENDING_TOURNAMENT_KEY, String(id));
      openLoginModal(true);
      return;
    }

    if (!validate()) {
      toast.current?.show({
        severity: "error",
        summary: "Check the form",
        detail: "Please fill every required field before continuing.",
        life: 2500,
      });
      return;
    }

    try {
      setIsSubmitting(true);

      const payload = {
        tournament_id: Number(id),
        team_name: form.team_name.trim(),
        captain_jersey_number: Number(form.captain_jersey_number),
        players: players.map((player) => ({
          player_name: player.player_name,
          phone: player.phone,
          email: player.email,
          jersey_number: Number(player.jersey_number),
        })),
      };

      const response = await axiosAuth.post(`${BASE_URL}/add-teams`, payload);
      const data = response.data || {};
      const order = extractOrder(data);

      if (data.skip_payment || !order) {
        setSuccessState({ teamName: form.team_name, paid: false });
        toast.current?.show({
          severity: "success",
          summary: "Registered",
          detail: "Your team has been registered.",
          life: 3000,
        });
        setIsSubmitting(false);
        return;
      }

      // keep the button disabled while the Razorpay popup is open
      openRazorpay(order, data.key || data.razorpay_key);
    } catch (error) {
      const data = error.response?.data;
      let message = data?.message || "Could not submit the team. Please try again.";

      if (data?.errors) {
        const flattened = {};
        Object.entries(data.errors).forEach(([key, value]) => {
          flattened[key] = Array.isArray(value) ? value[0] : value;
        });
        setErrors((prev) => ({ ...prev, ...flattened }));
        const first = Object.values(flattened)[0];
        if (first) message = String(first);
      }

      toast.current?.show({
        severity: "error",
        summary: "Could not register",
        detail: message,
        life: 3500,
      });
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <Default>
        <Loader />
      </Default>
    );
  }

  if (!tournament) {
    return (
      <Default>
        <div className="trn-page trn-register-page">
          <div className="bv-container bv-container--wide">
            <div className="af-empty bv-reveal trn-empty">
              <div className="af-empty__icon" aria-hidden="true">
                <i className="fa-solid fa-trophy" />
              </div>
              <h3>Tournament not found</h3>
              <p>This event may have been removed or is no longer available.</p>
              <Link to="/tournaments" className="bv-btn bv-btn--primary">
                Back to tournaments
              </Link>
            </div>
          </div>
        </div>
      </Default>
    );
  }

  return (
    <Default>
      <Helmet>
        <title>Register | {tournament.name} | Book Venue</title>
        <meta
          name="description"
          content={`Register your team for ${tournament.name} on Book Venue.`}
        />
      </Helmet>
      <Toast ref={toast} position="bottom-right" />

      <div className="trn-page trn-register-page">
        <header className="trn-hero trn-register-hero">
          <div className="bv-container bv-container--wide trn-hero__inner">
            <nav className="trn-breadcrumb" aria-label="Breadcrumb">
              <Link to="/" className="trn-breadcrumb__link">
                <i className="fa-solid fa-house" aria-hidden="true" />
                Home
              </Link>
              <span className="trn-breadcrumb__sep" aria-hidden="true">
                <i className="fa-solid fa-chevron-right" />
              </span>
              <Link to="/tournaments" className="trn-breadcrumb__link">
                Tournaments
              </Link>
              <span className="trn-breadcrumb__sep" aria-hidden="true">
                <i className="fa-solid fa-chevron-right" />
              </span>
              <span className="trn-breadcrumb__current" aria-current="page">
                Register
              </span>
            </nav>

            <div className="trn-hero__body trn-register-hero__body">
              <div className="trn-hero__content">
                <span className="trn-hero__eyebrow">
                  <i className="fa-solid fa-clipboard-list" aria-hidden="true" />
                  Team registration
                </span>
                <h1 className="trn-hero__title">
                  Register for{" "}
                  <span className="gradientText">{tournament.name}</span>
                </h1>
                <p className="trn-hero__lede">
                  Add your squad, confirm the captain, and complete a secure
                  payment to lock your place.
                </p>
                <div className="trn-register-pills">
                  <span>
                    <i className="fa-solid fa-location-dot" aria-hidden="true" />
                    {tournament.facility_name}
                  </span>
                  <span>
                    <i className="fa-regular fa-calendar" aria-hidden="true" />
                    {formatTournamentDate(
                      tournament.start_date,
                      tournament.end_date,
                    )}
                  </span>
                  <span>
                    <i className="fa-solid fa-futbol" aria-hidden="true" />
                    {tournament.sport_name}
                  </span>
                </div>
              </div>
              <div className="trn-hero__visual">
                <LazyImage
                  className="trn-hero__img"
                  src={tournament.image || bannerBv}
                  alt={tournament.name}
                  loading="eager"
                />
              </div>
            </div>
          </div>
        </header>

        <section className="bv-section--cream trn-register-section">
          <div className="bv-container bv-container--wide">
            {!isAuthed ? (
              <div className="trn-register-success">
                <div className="trn-register-success__icon" aria-hidden="true">
                  <i className="fa-solid fa-right-to-bracket" />
                </div>
                <h2>Login to register</h2>
                <p>
                  Sign in to fill your team details, add players, and complete
                  payment for this tournament.
                </p>
                <div className="trn-register-success__actions">
                  <button
                    type="button"
                    className="trn-card__cta"
                    onClick={() => {
                      sessionStorage.setItem(PENDING_TOURNAMENT_KEY, String(id));
                      openLoginModal(true);
                    }}
                  >
                    Continue to login
                    <span className="trn-card__cta-icon" aria-hidden="true">
                      <i className="fa-solid fa-arrow-right" />
                    </span>
                  </button>
                  <Link to="/tournaments" className="trn-register-ghost">
                    Back to tournaments
                  </Link>
                </div>
              </div>
            ) : successState ? (
              <div className="trn-register-success">
                <div className="trn-register-success__icon" aria-hidden="true">
                  <i className="fa-solid fa-circle-check" />
                </div>
                <h2>{successState.teamName} is in</h2>
                <p>
                  {successState.paid
                    ? "Payment is confirmed. We’ll share fixtures and match updates with your captain."
                    : "Your team has been registered for this tournament."}
                </p>
                <div className="trn-register-success__actions">
                  <Link to="/tournaments" className="trn-card__cta">
                    Browse more events
                    <span className="trn-card__cta-icon" aria-hidden="true">
                      <i className="fa-solid fa-arrow-right" />
                    </span>
                  </Link>
                  <button
                    type="button"
                    className="trn-register-ghost"
                    onClick={() => navigate("/dashboard")}
                  >
                    Go to dashboard
                  </button>
                </div>
              </div>
            ) : (
              <div className="trn-register-layout">
                <form className="trn-register-form" onSubmit={handleSubmit} noValidate>
                  <section className="trn-register-card">
                    <div className="trn-register-card__head">
                      <span className="trn-register-step">01</span>
                      <div>
                        <h2>Team details</h2>
                        <p>Enter your team name and jersey number. Captain name, mobile and email are taken from your account.</p>
                      </div>
                    </div>

                    <div className="trn-register-grid">
                      {TEAM_FIELDS.map((field) => (
                        <div key={field.name} className="trn-register-field">
                          <label htmlFor={`team-${field.name}`}>
                            <i className={`fa-solid ${field.icon}`} aria-hidden="true" />
                            {field.label}
                            {field.required ? <em>*</em> : <span>Optional</span>}
                          </label>

                          <input
                            id={`team-${field.name}`}
                            type={field.type}
                            min={field.type === "number" ? "0" : undefined}
                            placeholder={field.placeholder}
                            value={form[field.name]}
                            onChange={(event) =>
                              updateField(field.name, event.target.value)
                            }
                          />

                          {errors[field.name] ? (
                            <small className="trn-register-error">
                              {errors[field.name]}
                            </small>
                          ) : null}
                        </div>
                      ))}

                      {captainFields.map((item) => (
                        <div
                          key={item.key}
                          className={`trn-register-field ${
                            item.full ? "trn-register-field--full" : ""
                          }`}
                        >
                          <label htmlFor={`captain-${item.key}`}>
                            <i
                              className={`fa-solid ${item.icon}`}
                              aria-hidden="true"
                            />
                            {item.label}
                            <span>From your account</span>
                          </label>
                          <input
                            id={`captain-${item.key}`}
                            type="text"
                            value={item.value || (profileLoading ? "Loading..." : "—")}
                            readOnly
                            tabIndex={-1}
                            aria-readonly="true"
                          />
                        </div>
                      ))}
                    </div>
                  </section>

                  <section className="trn-register-card">
                    <div className="trn-register-card__head">
                      <span className="trn-register-step">02</span>
                      <div>
                        <h2>Squad</h2>
                        <p>Add your players one at a time.</p>
                      </div>
                      <button
                        type="button"
                        className="trn-register-add"
                        onClick={openAddPlayer}
                      >
                        <i className="fa-solid fa-plus" aria-hidden="true" />
                        Add player
                      </button>
                    </div>

                    {errors.players ? (
                      <p className="trn-register-error">{errors.players}</p>
                    ) : null}

                    {players.length === 0 ? (
                      <div className="trn-player-empty">
                        <i className="fa-solid fa-users" aria-hidden="true" />
                        <h3>No players yet</h3>
                        <p>Use Add player to open the form and save each squad member.</p>
                      </div>
                    ) : (
                      <ul className="trn-player-list">
                        {players.map((player, index) => (
                          <li
                            key={`${player.phone}-${index}`}
                            className="trn-player-row"
                          >
                            <span className="trn-player-row__avatar" aria-hidden="true">
                              {player.player_name.charAt(0).toUpperCase()}
                            </span>
                            <div className="trn-player-row__meta">
                              <strong>{player.player_name}</strong>
                              <small>
                                {player.phone} · {player.email} · #{player.jersey_number}
                              </small>
                            </div>
                            <div className="trn-player-row__actions">
                              <button
                                type="button"
                                onClick={() => openEditPlayer(index)}
                                aria-label={`Edit ${player.player_name}`}
                              >
                                <i className="fa-solid fa-pen" />
                              </button>
                              <button
                                type="button"
                                className="is-danger"
                                onClick={() => removePlayer(index)}
                                aria-label={`Remove ${player.player_name}`}
                              >
                                <i className="fa-solid fa-trash" />
                              </button>
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </section>

                  <div className="trn-register-mobile-pay">
                    <button
                      type="submit"
                      className="trn-hero__cta"
                      disabled={isSubmitting}
                    >
                      {isSubmitting
                        ? "Please wait..."
                        : entryAmount
                          ? `Pay ${formatFee(tournament.entry_fee)} & register`
                          : "Submit team"}
                      <span className="trn-hero__cta-icon" aria-hidden="true">
                        <i className="fa-solid fa-arrow-right" />
                      </span>
                    </button>
                  </div>
                </form>

                <aside className="trn-register-aside">
                  <div className="trn-register-summary">
                    <h2>Entry summary</h2>
                    <dl>
                      <div>
                        <dt>Tournament</dt>
                        <dd>{tournament.name}</dd>
                      </div>
                      <div>
                        <dt>Venue</dt>
                        <dd>{tournament.facility_name}</dd>
                      </div>
                      <div>
                        <dt>Squad size</dt>
                        <dd>
                          {players.length + 1} / {maxPlayers} players (incl. you)
                        </dd>
                      </div>
                      <div>
                        <dt>Entry fee</dt>
                        <dd className="trn-register-summary__fee">
                          {formatFee(tournament.entry_fee)}
                        </dd>
                      </div>
                      {tournament.prize_details ? (
                        <div>
                          <dt>Prize pool</dt>
                          <dd>{tournament.prize_details}</dd>
                        </div>
                      ) : null}
                    </dl>
                    <button
                      type="button"
                      className="trn-hero__cta trn-register-summary__cta"
                      disabled={isSubmitting}
                      onClick={handleSubmit}
                    >
                      {isSubmitting
                        ? "Please wait..."
                        : entryAmount
                          ? "Continue to payment"
                          : "Submit registration"}
                      <span className="trn-hero__cta-icon" aria-hidden="true">
                        <i className="fa-solid fa-lock" />
                      </span>
                    </button>
                    <p className="trn-register-note">
                      <i className="fa-solid fa-shield-halved" aria-hidden="true" />
                      Secure Razorpay checkout · Instant confirmation
                    </p>
                  </div>
                </aside>
              </div>
            )}
          </div>
        </section>
      </div>

      {playerModalOpen ? (
        <div
          className="trn-player-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="trn-player-modal-title"
        >
          <button
            type="button"
            className="trn-player-modal__backdrop"
            aria-label="Close add player form"
            onClick={closePlayerModal}
          />
          <div className="trn-player-modal__panel">
            <header className="trn-player-modal__head">
              <div>
                <span className="trn-hero__eyebrow">
                  <i className="fa-solid fa-user-plus" aria-hidden="true" />
                  Squad member
                </span>
                <h3 id="trn-player-modal-title">
                  {editingPlayerIndex === null ? "Add player" : "Edit player"}
                </h3>
                <p>Fill this form and save. Then add the next player.</p>
              </div>
              <button
                type="button"
                className="trn-player-remove"
                onClick={closePlayerModal}
                aria-label="Close"
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </header>

            <div className="trn-player-grid">
              <label>
                Full name
                <input
                  type="text"
                  value={playerDraft.player_name}
                  placeholder="Player name"
                  onChange={(event) =>
                    updatePlayerDraft("player_name", event.target.value)
                  }
                />
                {playerDraftErrors.player_name ? (
                  <small className="trn-register-error">
                    {playerDraftErrors.player_name}
                  </small>
                ) : null}
              </label>
              <label>
                Phone
                <input
                  type="tel"
                  maxLength={10}
                  value={playerDraft.phone}
                  placeholder="10-digit phone"
                  onChange={(event) =>
                    updatePlayerDraft(
                      "phone",
                      event.target.value.replace(/\D/g, "").slice(0, 10),
                    )
                  }
                />
                {playerDraftErrors.phone ? (
                  <small className="trn-register-error">
                    {playerDraftErrors.phone}
                  </small>
                ) : null}
              </label>
              <label>
                Email
                <input
                  type="email"
                  value={playerDraft.email}
                  placeholder="name@email.com"
                  onChange={(event) =>
                    updatePlayerDraft("email", event.target.value)
                  }
                />
                {playerDraftErrors.email ? (
                  <small className="trn-register-error">
                    {playerDraftErrors.email}
                  </small>
                ) : null}
              </label>
              <label>
                Jersey number
                <input
                  type="number"
                  min="0"
                  value={playerDraft.jersey_number}
                  placeholder="e.g. 7"
                  onChange={(event) =>
                    updatePlayerDraft("jersey_number", event.target.value)
                  }
                />
                {playerDraftErrors.jersey_number ? (
                  <small className="trn-register-error">
                    {playerDraftErrors.jersey_number}
                  </small>
                ) : null}
              </label>
            </div>

            <div className="trn-player-modal__actions">
              <button
                type="button"
                className="trn-register-ghost"
                onClick={closePlayerModal}
              >
                Cancel
              </button>
              <button type="button" className="trn-card__cta" onClick={savePlayer}>
                {editingPlayerIndex === null ? "Save player" : "Update player"}
                <span className="trn-card__cta-icon" aria-hidden="true">
                  <i className="fa-solid fa-check" />
                </span>
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </Default>
  );
};

export default RegisterTournament;